/* ============================================
   WORK SUMMARY COMPONENT
   The Creative Orchestra - GEMINI Framework
   Summary and description section for work pages
   ============================================ */

import { motion } from 'framer-motion';
import { 
  Lightbulb, 
  Zap, 
  Target, 
  CheckCircle2,
  Code,
  Database,
  Cpu,
  Globe,
} from 'lucide-react';
import { useTheme } from '@context/ThemeContext';
import { ScrollReveal } from '@components/common/ScrollToTop';

/**
 * Work Summary Component
 * Displays work summary, description, features, and key insights
 * 
 * @param {Object} props
 * @param {Object} props.work - Work data object
 */
const WorkSummary = ({ work }) => {
  const { activeTheme } = useTheme();
  const accentColor = activeTheme?.colors?.accent || work.colorTheme?.accent || '#818CF8';
  const accentRgb = activeTheme?.cssVariables?.['--theme-accent-rgb'] || '129, 140, 248';

  return (
    <section className="py-16 md:py-24 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Main summary */}
        <ScrollReveal>
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-white mb-6">
              Overview
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              {work.summary}
            </p>
          </div>
        </ScrollReveal>

        {/* Detailed description */}
        {work.description && work.description.length > 0 && (
          <ScrollReveal delay={0.1}>
            <div className="mb-16">
              <div className="max-w-3xl mx-auto space-y-6">
                {work.description.map((paragraph, index) => (
                  <p 
                    key={index}
                    className="text-gray-400 leading-relaxed"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </ScrollReveal>
        )}

        {/* Features grid */}
        {work.features && work.features.length > 0 && (
          <ScrollReveal delay={0.2}>
            <div className="mb-16">
              <h3 className="text-xl font-semibold text-white mb-8 text-center">
                Key Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {work.features.map((feature, index) => (
                  <FeatureCard 
                    key={index}
                    feature={feature}
                    index={index}
                    accentColor={accentColor}
                    accentRgb={accentRgb}
                  />
                ))}
              </div>
            </div>
          </ScrollReveal>
        )}

        {/* Two column layout: Insights + Technical Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Key Insights */}
          {work.keyInsights && work.keyInsights.length > 0 && (
            <ScrollReveal delay={0.3}>
              <InsightsCard 
                insights={work.keyInsights}
                accentColor={accentColor}
                accentRgb={accentRgb}
              />
            </ScrollReveal>
          )}

          {/* Technical Details */}
          {work.technicalDetails && (
            <ScrollReveal delay={0.4}>
              <TechnicalDetailsCard 
                details={work.technicalDetails}
                accentColor={accentColor}
                accentRgb={accentRgb}
              />
            </ScrollReveal>
          )}
        </div>

        {/* Metrics (if available) */}
        {work.metrics && Object.keys(work.metrics).length > 0 && (
          <ScrollReveal delay={0.5}>
            <MetricsSection 
              metrics={work.metrics}
              accentColor={accentColor}
              accentRgb={accentRgb}
            />
          </ScrollReveal>
        )}
      </div>
    </section>
  );
};

/**
 * Feature Card Component
 */
const FeatureCard = ({ feature, index, accentColor, accentRgb }) => {
  return (
    <motion.div
      className="p-4 rounded-xl bg-white/5 border border-white/10
        hover:bg-white/10 hover:border-white/20 transition-all"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -2 }}
    >
      <div className="flex items-start gap-3">
        <div 
          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
          style={{ background: `rgba(${accentRgb}, 0.2)` }}
        >
          <CheckCircle2 
            className="w-4 h-4" 
            style={{ color: accentColor }}
          />
        </div>
        <p className="text-gray-300 text-sm leading-relaxed">
          {feature}
        </p>
      </div>
    </motion.div>
  );
};

/**
 * Insights Card Component
 */
const InsightsCard = ({ insights, accentColor, accentRgb }) => {
  return (
    <div 
      className="p-6 rounded-2xl border"
      style={{
        background: `rgba(${accentRgb}, 0.05)`,
        borderColor: `rgba(${accentRgb}, 0.1)`,
      }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: `rgba(${accentRgb}, 0.2)` }}
        >
          <Lightbulb className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <h3 className="text-lg font-semibold text-white">Key Insights</h3>
      </div>
      
      <ul className="space-y-4">
        {insights.map((insight, index) => (
          <li key={index} className="flex items-start gap-3">
            <span 
              className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center
                text-xs font-bold mt-0.5"
              style={{ 
                background: `rgba(${accentRgb}, 0.2)`,
                color: accentColor,
              }}
            >
              {index + 1}
            </span>
            <p className="text-gray-400 text-sm leading-relaxed">
              {insight}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Technical Details Card Component
 */
const TechnicalDetailsCard = ({ details, accentColor, accentRgb }) => {
  const techIcons = {
    platform: Globe,
    engine: Cpu,
    language: Code,
    database: Database,
    frontend: Code,
    backend: Code,
    aiModel: Cpu,
    deployment: Globe,
  };

  return (
    <div 
      className="p-6 rounded-2xl border"
      style={{
        background: `rgba(${accentRgb}, 0.05)`,
        borderColor: `rgba(${accentRgb}, 0.1)`,
      }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: `rgba(${accentRgb}, 0.2)` }}
        >
          <Code className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <h3 className="text-lg font-semibold text-white">Technical Stack</h3>
      </div>
      
      <div className="space-y-3">
        {Object.entries(details).map(([key, value]) => {
          const Icon = techIcons[key] || Zap;
          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          
          return (
            <div key={key} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-500">{label}</span>
              </div>
              <span className="text-sm text-gray-300 font-medium">
                {value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Metrics Section Component
 */
const MetricsSection = ({ metrics, accentColor, accentRgb }) => {
  return (
    <div className="mt-16">
      <h3 className="text-xl font-semibold text-white mb-8 text-center">
        By the Numbers
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(metrics).map(([key, value], index) => {
          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          
          return (
            <motion.div
              key={key}
              className="p-6 rounded-xl text-center"
              style={{
                background: `rgba(${accentRgb}, 0.05)`,
                borderColor: `rgba(${accentRgb}, 0.1)`,
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <div 
                className="text-3xl md:text-4xl font-bold mb-2"
                style={{ color: accentColor }}
              >
                {value}
              </div>
              <div className="text-sm text-gray-500">
                {label}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Agents Section (for works with agent systems)
 */
export const AgentsSection = ({ agents, accentColor, accentRgb }) => {
  if (!agents || agents.length === 0) return null;

  return (
    <section className="py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <h3 className="text-2xl font-semibold text-white mb-8 text-center">
          AI Agents
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((agent, index) => (
            <motion.div
              key={agent.name}
              className="p-4 rounded-xl bg-white/5 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
            >
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                style={{ background: `rgba(${accentRgb}, 0.2)` }}
              >
                <Target className="w-5 h-5" style={{ color: accentColor }} />
              </div>
              <h4 className="text-white font-semibold mb-1">{agent.name}</h4>
              <p className="text-sm text-gray-400">{agent.role}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WorkSummary;
